import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { User, Wallet, Transaction, Order, WalletType } from '../types';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://yfapi.tch.co.za/api/v1'
    : 'http://192.168.178.190:5001/api/v1');

// The /health endpoint lives at the server root (not under /api/v1). Derive its base
// from API_BASE_URL so it works whether the API is same-origin (bundled image → "/api/v1"
// gives "") or a separate host in dev.
const HEALTH_BASE_URL = API_BASE_URL.replace(/\/api\/v\d+\/?$/, '');

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const TOKEN_KEY = 'yebo_wallet_token';

export const getAuthToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setAuthToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const removeAuthToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  delete apiClient.defaults.headers.common['Authorization'];
};

// Initialize token on app start
const savedToken = getAuthToken();
if (savedToken) {
  setAuthToken(savedToken);
}

// Request interceptor for debugging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`API Response: ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    
    // Handle unauthorized errors
    if (error.response?.status === 401) {
      removeAuthToken();
      // Redirect to login if needed
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// API Response interfaces
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp?: string;
}

interface AuthResponse {
  user: User;
  token: string;
}

interface WalletSummary {
  totalBalance: number;
  totalAvailableBalance: number;
  totalReservedAmount: number;
  walletsCount: number;
}

// Authentication API
export const authApi = {
  login: async (pin: string, phone?: string): Promise<AuthResponse> => {
    const loginData = phone ? { pin, phone } : { pin };
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth/login', loginData);
    const { user, token } = response.data.data;
    setAuthToken(token);
    return { user, token };
  },

  register: async (userData: {
    name: string;
    phone: string;
    email?: string;
    pin: string;
  }): Promise<AuthResponse> => {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth/register', userData);
    const { user, token } = response.data.data;
    setAuthToken(token);
    return { user, token };
  },

  verifyToken: async (): Promise<User> => {
    const response = await apiClient.post<ApiResponse<{ user: User }>>('/auth/verify-token');
    return response.data.data.user;
  },

  logout: (): void => {
    removeAuthToken();
  },

  verifyPin: async (pin: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse<void>>('/auth/verify-pin', { pin });
    return response.data.data;
  },

  forgotPin: async (phone: string): Promise<string> => {
    const response = await apiClient.post<ApiResponse<unknown>>('/auth/forgot-pin', { phone });
    return response.data.message;
  },
};

// Users API
export const usersApi = {
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get<ApiResponse<User>>('/users/profile');
    return response.data.data;
  },

  updateProfile: async (userData: {
    name?: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
    address?: string;
    idNumber?: string;
  }): Promise<User> => {
    const response = await apiClient.put<ApiResponse<User>>('/users/profile', userData);
    return response.data.data;
  },
};

// Wallets API
export const walletsApi = {
  getWallets: async (): Promise<Wallet[]> => {
    const response = await apiClient.get<ApiResponse<Wallet[]>>('/wallets');
    return response.data.data;
  },

  getWallet: async (walletId: string): Promise<Wallet> => {
    const response = await apiClient.get<ApiResponse<Wallet>>(`/wallets/${walletId}`);
    return response.data.data;
  },

  getWalletSummary: async (): Promise<WalletSummary> => {
    const response = await apiClient.get<ApiResponse<WalletSummary>>('/wallets/summary/totals');
    return response.data.data;
  },

  topUpWallet: async (walletId: string, amount: number, description?: string, reference?: string): Promise<{
    amount: number;
    newTotalBalance: number;
    newAvailableBalance: number;
  }> => {
    const response = await apiClient.post<ApiResponse<{
      amount: number;
      newTotalBalance: number;
      newAvailableBalance: number;
    }>>(`/wallets/${walletId}/top-up`, {
      amount,
      description,
      reference,
    });
    return response.data.data;
  },

  validateWalletId: async (walletId: string): Promise<{
    walletId: string;
    name: string;
    phone: string;
  }> => {
    const response = await apiClient.get<ApiResponse<{
      walletId: string;
      name: string;
      phone: string;
    }>>(`/wallets/validate/${walletId}`);
    return response.data.data;
  },

  transferMoney: async (transferData: {
    fromWalletId: string;
    toWalletId: string;
    amount: number;
    reference?: string;
  }): Promise<{
    transactionId: string;
    amount: number;
    recipientName: string;
    recipientWalletId: string;
    senderNewBalance: number;
  }> => {
    const response = await apiClient.post<ApiResponse<{
      transactionId: string;
      amount: number;
      recipientName: string;
      recipientWalletId: string;
      senderNewBalance: number;
    }>>('/wallets/transfer', transferData);
    return response.data.data;
  },
};

// Transactions API
export const transactionsApi = {
  getTransactions: async (filters?: {
    walletId?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<Transaction[]> => {
    const params = new URLSearchParams();
    if (filters?.walletId) params.append('walletId', filters.walletId);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await apiClient.get<ApiResponse<Transaction[]>>(`/transactions?${params}`);
    return response.data.data;
  },
};

// Orders API
export const ordersApi = {
  getOrders: async (filters?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<Order[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await apiClient.get<ApiResponse<Order[]>>(`/orders?${params}`);
    return response.data.data;
  },

  payOrder: async (orderId: string, walletPayments: { walletId: string; amount: number }[], pin: string): Promise<{
    orderId: string;
    totalAmount: number;
    walletPayments: { walletId: string; amount: number }[];
  }> => {
    const response = await apiClient.post<ApiResponse<{
      orderId: string;
      totalAmount: number;
      walletPayments: { walletId: string; amount: number }[];
    }>>(`/orders/${orderId}/pay`, {
      walletPayments,
      pin,
    });
    return response.data.data;
  },

  cancelOrder: async (orderId: string): Promise<{
    orderId: string;
    status: string;
    orderNumber: string;
  }> => {
    const response = await apiClient.post<ApiResponse<{
      orderId: string;
      status: string;
      orderNumber: string;
    }>>(`/orders/${orderId}/cancel`);
    return response.data.data;
  },

  getOrderStatus: async (orderId: string): Promise<{
    orderId: string;
    orderNumber: string;
    status: string;
    totalAmount: number;
    storeName: string;
    createdAt: string;
    updatedAt: string;
  }> => {
    const response = await apiClient.get<ApiResponse<{
      orderId: string;
      orderNumber: string;
      status: string;
      totalAmount: number;
      storeName: string;
      createdAt: string;
      updatedAt: string;
    }>>(`/orders/${orderId}/status`);
    return response.data.data;
  },
};

// Health API (for monitoring)
export const healthApi = {
  getHealth: async (): Promise<{
    success: boolean;
    status: string;
    timestamp: string;
  }> => {
    const response = await apiClient.get('/health', {
      baseURL: HEALTH_BASE_URL, // same-origin health endpoint (server root)
    });
    return response.data;
  },

  getStatus: async (): Promise<any> => {
    const response = await apiClient.get('/health/status', {
      baseURL: HEALTH_BASE_URL, // same-origin health endpoint (server root)
    });
    return response.data;
  },
};

// Utility functions
export const formatCurrency = (amount: number, currency: string = 'ZAR'): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

export const generateUniqueReference = (): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 5);
  return `ABC${timestamp}${random}`.toUpperCase().substr(0, 10);
};

// ---- Admin API (separate token from the consumer wallet session) ----
const ADMIN_TOKEN_KEY = 'yebo_admin_token';

const adminClient: AxiosInstance = axios.create({ baseURL: API_BASE_URL });
adminClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const adminApi = {
  setToken(token: string) { localStorage.setItem(ADMIN_TOKEN_KEY, token); },
  clearToken() { localStorage.removeItem(ADMIN_TOKEN_KEY); },
  hasToken(): boolean { return !!localStorage.getItem(ADMIN_TOKEN_KEY); },

  async login(username: string, password: string) {
    const res = await axios.post(`${API_BASE_URL}/auth/login/username`, { username, password });
    return res.data.data as { user: any; token: string };
  },

  async unallocated() {
    const res = await adminClient.get('/admin/shop2shop/unallocated');
    return res.data.data as any[];
  },
  async searchUsers(q: string) {
    const res = await adminClient.get('/admin/users/search', { params: { q } });
    return res.data.data as any[];
  },
  async allocate(id: string, body: { userId?: string; walletId?: string }) {
    const res = await adminClient.post(`/admin/shop2shop/${id}/allocate`, body);
    return res.data.data;
  },
  async dismiss(id: string, note: string) {
    const res = await adminClient.post(`/admin/shop2shop/${id}/dismiss`, { note });
    return res.data.data;
  },
  async report(params: { from: string; to: string; status?: string; limit?: number; offset?: number }) {
    const res = await adminClient.get('/admin/shop2shop/report', { params });
    return res.data as { data: any[]; summary: any; paging: any };
  },
  async ordersByUser(params: { from: string; to: string }) {
    const res = await adminClient.get('/admin/orders/by-user', { params });
    return res.data.data as any[];
  },
  async markExternalPaid(orderIds: string[]) {
    const res = await adminClient.post('/admin/orders/mark-external-paid', { orderIds });
    return res.data.data as { markedCount: number; skippedCount: number; totalAmount: number };
  },
  async externallyPaid(params: { from: string; to: string }) {
    const res = await adminClient.get('/admin/orders/externally-paid', { params });
    return res.data as { data: any[]; grandTotal: number };
  },
  async importUsers(file: File) {
    const form = new FormData();
    form.append('file', file);
    const res = await adminClient.post('/admin/users/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return res.data.data as {
      totalRows: number;
      created: number;
      skipped: number;
      errors: Array<{ row: number; phone?: string; reason: string }>;
    };
  },
};

// Export default API object
const api = {
  auth: authApi,
  users: usersApi,
  wallets: walletsApi,
  transactions: transactionsApi,
  orders: ordersApi,
  health: healthApi,
};

export default api;